/**
 * Date range selector with presets for the Ads Manager page.
 */

import { useRef, useState } from "react"
import { DateRangePicker } from "@/components/DateRangePicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { labelFromIsoRange } from "@/pages/dashboard/lib/presets"
import {
  computePresetRange,
  ROOTS_REPORTING_TIMEZONE,
  type AdsManagerDatePreset,
} from "../lib/datePresets"

export type {
  AdsManagerDatePreset,
  AdsManagerReportingTab,
  PresetTimezoneMode,
} from "../lib/datePresets"

/** Toolbar chips / mobile select only — Last 7 days lives in the calendar popup. */
const PRESETS: { value: AdsManagerDatePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "thisweek", label: "This week" },
]

interface DateRangeSelectorProps {
  datePreset: AdsManagerDatePreset
  startDate: string
  endDate: string
  displayLabel: string
  onPresetChange: (
    preset: AdsManagerDatePreset,
    startDate: string,
    endDate: string,
    displayLabel: string,
  ) => void
  onCustomRangeApply: (
    startDate: string,
    endDate: string,
    displayLabel: string,
  ) => void
  useReportingTimezone?: boolean
  reportingTimeZone?: string
  /** Inclusive max custom/preset span in days (e.g. 30 for Detail modal). */
  maxRangeDays?: number
}

export function DateRangeSelector({
  datePreset,
  startDate,
  endDate,
  displayLabel,
  onPresetChange,
  onCustomRangeApply,
  useReportingTimezone = false,
  reportingTimeZone = ROOTS_REPORTING_TIMEZONE,
  maxRangeDays,
}: DateRangeSelectorProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const desktopWrapRef = useRef<HTMLDivElement>(null)
  const mobileWrapRef = useRef<HTMLDivElement>(null)
  /** Points at the trigger that opened the picker (portal position + outside-click). */
  const activeBoundaryRef = useRef<HTMLElement | null>(null)

  const handlePreset = (preset: AdsManagerDatePreset) => {
    const range = computePresetRange(
      preset,
      useReportingTimezone ? "reporting" : "local",
      reportingTimeZone,
    )
    onPresetChange(preset, range.startDate, range.endDate, range.displayLabel)
  }

  // Presets not on the toolbar (e.g. last7 default in Detail) show as the
  // custom chip so the mobile Select still has a matching value.
  const selectValue = PRESETS.some((p) => p.value === datePreset)
    ? datePreset
    : "custom"

  const openPicker = (anchor: HTMLElement | null) => {
    activeBoundaryRef.current = anchor
    setPickerOpen(true)
  }

  const handleApply = (s: string, e: string, label: string) => {
    onCustomRangeApply(s, e, labelFromIsoRange(s, e) || label)
    setPickerOpen(false)
  }

  return (
    <div className="flex w-full min-w-0 shrink-0 flex-nowrap items-center gap-1 md:inline-flex md:w-auto">
      {/* Compact preset dropdown on narrow screens */}
      <div ref={mobileWrapRef} className="relative min-w-0 flex-1 md:hidden">
        <Select
          value={selectValue}
          onValueChange={(value) => {
            if (value === "custom") {
              openPicker(mobileWrapRef.current)
              return
            }
            handlePreset(value as AdsManagerDatePreset)
          }}
        >
          <SelectTrigger className="h-9 w-full border border-[#555] bg-[#2d2d2d] text-xs text-white focus:ring-0">
            <SelectValue
              placeholder="Date range"
              aria-label={
                selectValue === "custom" ? displayLabel : undefined
              }
            />
          </SelectTrigger>
          <SelectContent className="border-[#555] bg-[#2d2d2d] text-white">
            {PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
            <SelectItem value="custom">
              {selectValue === "custom" ? displayLabel : "Custom range…"}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Full preset buttons on md+ */}
      <div className="hidden shrink-0 flex-nowrap items-center gap-1 md:inline-flex">
        {PRESETS.map((p) => (
          <button
            key={p.value}
            type="button"
            onClick={() => handlePreset(p.value)}
            className={`h-7 rounded-md border px-2.5 text-xs font-medium shadow-sm transition-colors ${
              datePreset === p.value
                ? "border-[#8fa0ef]/35 bg-[#7488e8] text-white"
                : "border-[#555] bg-[#2d2d2d] text-white hover:bg-[#3a3a3a]"
            }`}
          >
            {p.label}
          </button>
        ))}
        <div ref={desktopWrapRef} className="relative">
          <button
            type="button"
            onClick={() => {
              if (pickerOpen) {
                setPickerOpen(false)
                return
              }
              openPicker(desktopWrapRef.current)
            }}
            className="flex h-9 min-w-0 flex-1 items-center gap-1.5 rounded-md border border-[#555] bg-[#2d2d2d] px-2.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-[#3a3a3a] md:h-7 md:max-w-[140px] md:flex-none"
            aria-expanded={pickerOpen}
            aria-haspopup="dialog"
            aria-label="Custom date range"
          >
            <span className="truncate">{displayLabel}</span>
            <span className="text-[0.7rem]" aria-hidden>
              &#9660;
            </span>
          </button>
        </div>
      </div>

      {/* One picker only — two mounted instances both listened for outside-click
          and the hidden one closed the portal before quick-preset clicks fired. */}
      <DateRangePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        initialStart={startDate || null}
        initialEnd={endDate || null}
        outsideBoundaryRef={activeBoundaryRef}
        onApply={handleApply}
        maxRangeDays={maxRangeDays}
        resolveQuickPreset={(key) =>
          computePresetRange(
            key,
            useReportingTimezone ? "reporting" : "local",
            reportingTimeZone,
          )
        }
      />
    </div>
  )
}
